<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import DrawerCloseButton from "../drawer/DrawerCloseButton.vue";
import SheetTitleBar from "../drawer/DrawerTitleBar.vue";
const emits = defineEmits(["updateVisible", "showCloseButton"]);
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  canClose: {
    type: Boolean,
    default: true
  },
  closeTrigger: {
    type: Boolean,
    default: false
  },
  position: {
    type: String,
    default: "left",
    validator: (value) => ["left", "right"].includes(value)
  },
  forcedMobile: {
    type: Boolean,
    default: false
  },
  closeHeight: {
    type: Number,
    default: 75
  },
  sheetWidth: {
    type: Number,
    default: 488
  },
  forceFullSize: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: void 0
  },
  defaultSheetHeight: {
    type: Number,
    default: 400
  }
});
const sheetVisible = ref(props.visible);
const sheetHeight = ref(0);
const isResizing = ref(false);
const initialY = ref(0);
const initialHeight = ref(0);
const isFullSize = ref(props.forceFullSize);
const drawCloseButton = ref(props.showCloseButton);
const windowWidth = ref(0);
const animateSize = ref(false);
const fakeDivHeight = ref(0);
const fakeDivWidth = ref(0);
const gotOpened = ref(false);
const resizeObserver = ref(null);
const timeout = ref(null);
const interval = ref(null);
const sheetRef = ref(null);
const updateShouldDrawButton = () => {
  drawCloseButton.value = isFullSize.value || props.showCloseButton;
};
const onResize = () => {
  windowWidth.value = window.outerWidth;
  animateSize.value = sheetVisible.value;
  clearTimeout(timeout.value);
  timeout.value = setTimeout(() => {
    animateSize.value = true;
  }, 100);
};
const onResizeSheet = () => {
  fakeDivHeight.value = Math.max(
    sheetRef.value.clientHeight - 10,
    0
  );
};
const close = () => {
  emits("updateVisible", false);
};
const startResize = (e) => {
  e.preventDefault();
  isResizing.value = true;
  initialY.value = e.clientY || e.touches[0]?.clientY || 0;
  initialHeight.value = sheetHeight.value;
  document.addEventListener("mousemove", handleResize);
  document.addEventListener("touchmove", handleResize, {
    passive: false
  });
  document.addEventListener("mouseup", stopResize);
  document.addEventListener("touchend", stopResize);
};
const handleResize = (e) => {
  e.preventDefault();
  if (!isResizing.value) return;
  const currentY = e.clientY || e.touches[0]?.clientY || 0;
  const deltaY = currentY - initialY.value;
  const newHeight = initialHeight.value - deltaY;
  const windowHeight = window.innerHeight - 20;
  sheetHeight.value = Math.min(windowHeight, newHeight);
  if (newHeight < props.closeHeight) {
    if (props.canClose) {
      closeSheet();
      sheetHeight.value = 0;
    } else {
      sheetHeight.value = props.closeHeight;
    }
  }
  if (newHeight > windowHeight - 75) {
    isFullSize.value = true;
    stopResize();
  }
};
const closeSheet = () => {
  if (isFullSize.value && !props.forceFullSize) {
    softCloseSheet();
  } else {
    close();
  }
  stopResize();
};
const softCloseSheet = () => {
  isFullSize.value = false;
  sheetHeight.value = props.defaultSheetHeight;
};
const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener("mousemove", handleResize);
  document.removeEventListener("touchmove", handleResize);
  document.removeEventListener("mouseup", stopResize);
  document.removeEventListener("touchend", stopResize);
};
const animationStart = () => {
  if (isResizing.value || !gotOpened.value) return;
  if (interval.value) {
    clearInterval(interval.value);
  }
  interval.value = setInterval(() => {
    if (props.position === "left") {
      fakeDivWidth.value = props.sheetWidth + (sheetRef.value?.offsetLeft || 0);
    } else if (props.position === "right") {
      fakeDivWidth.value = window.outerWidth - (sheetRef.value?.offsetLeft || 0) + (window.outerWidth - window.innerWidth);
    }
  }, 1);
  setTimeout(() => {
    animationEnd();
  }, 700);
};
const animationEnd = () => {
  clearInterval(interval.value);
};
onMounted(() => {
  windowWidth.value = window.outerWidth;
  window.addEventListener("resize", onResize);
  animateSize.value = false;
  timeout.value = setTimeout(() => {
    animateSize.value = true;
  }, 100);
  resizeObserver.value = new ResizeObserver(onResizeSheet);
  resizeObserver.value.observe(sheetRef.value);
  fakeDivWidth.value = props.sheetWidth + (sheetRef.value?.offsetLeft || 0);
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
  resizeObserver.value?.unobserve(sheetRef.value);
});
watch(
  () => props.closeTrigger,
  (val) => {
    if (val) {
      if (props.canClose) {
        close();
      } else {
        softCloseSheet();
      }
    }
  }
);
watch(
  () => props.visible,
  (val) => {
    sheetVisible.value = val;
    if (val) {
      sheetHeight.value = props.defaultSheetHeight;
      if (document) {
        document.body.style.overflow = "hidden";
      }
    } else {
      sheetHeight.value = 0;
      if (document) {
        document.body.style.overflow = "auto";
      }
    }
    gotOpened.value = true;
    timeout.value = setTimeout(() => {
      gotOpened.value = false;
    }, 100);
  },
  { immediate: true }
);
watch(isFullSize, () => {
  updateShouldDrawButton();
});
watch(
  () => props.showCloseButton,
  (val) => {
    emits("showCloseButton", val);
  }
);
</script>

<template>
    <div
        class="fixed bottom-0 w-full h-full pointer-events-none left-0 z-50"
        :aria-hidden="!sheetVisible"
    >
        <div
            class="transition-transform ease-in-out duration-700 flex"
            :style="{
  height: `${fakeDivHeight}px !important`,
  width: `${fakeDivWidth}px !important`
}"
        />
        <div
            ref="sheetRef"
            class="mobile-sheet absolute flex flex-col gap-2 ease-in-out bottom-0 duration-700 w-full z-50 bg-white overflow-hidden"
            :class="[
  sheetVisible ? 'pointer-events-auto' : 'pointer-events-none',
  isResizing ? 'resizing transition-none' : 'transition-all',
  isFullSize ? 'h-full' : 'h-auto',
  animateSize ? 'transition-all' : 'transition-none',
  !isFullSize ? 'rounded-t-xl border-t border-slate-300' : '!rounded-t-none'
]"
            :style="{
  height: isFullSize && visible ? '100% !important' : sheetHeight + 'px !important'
}"
            @transitionstart="animationStart"
        >
            <!-- RESIZE HANDLE -->
            <div
                class="resize-handle justify-center"
                :class="[isFullSize ? 'hidden' : 'flex']"
                @mousedown="startResize"
                @dblclick="closeSheet"
                @touchstart="startResize"
            ></div>

            <!-- PILL RESIZE HANDLE FOR MOBILE -->
            <div
                class="sticky top-0 w-12 ml-auto pointer-events-none flex-shrink-0 transition-all ease-in-out duration-500 mr-auto bg-black bg-opacity-10 rounded-2xl mt-2"
                :class="[isFullSize ? 'h-0 -pb-2' : 'h-1.5']"
            />

            <DrawerCloseButton
                v-if="!title && drawCloseButton"
                class="absolute top-1 right-1"
                :show-close-button="showCloseButton"
                @closeDrawer="closeSheet"
            />

            <!-- CONTAINER -->
            <div
                class="w-full flex-col inline-flex relative overflow-hidden h-full"
                :class="[isResizing ? 'resizing' : '']"
            >
                <SheetTitleBar
                    v-if="title && drawCloseButton"
                    :show-close-button="drawCloseButton"
                    :title="title"
                    @closeDrawer="closeSheet"
                />
                <slot />
            </div>
        </div>
    </div>
</template>

<style scoped>
.mobile-sheet {
  @apply w-full z-50;
  box-shadow: 0px -4px 8px 0px rgba(19, 47, 68, 0.2), 0px 0px 1px 0px rgba(19, 47, 68, 0.1);
}

.resize-handle {
  @apply absolute top-0 left-0 w-full h-12 cursor-ns-resize;
}

.resizing {
  user-select: none;
}
</style>
